import { rpx } from 'client/lib/rpx-client';
import { showToast } from '@components/toaster';
import type { TrackerGlance } from 'server/dbx/tracker';
import { swallowTrackerLoadFailure } from '@course/components/checkins/load-failure';

export type { TrackerGlance };

/** An inline link whose padding reaches 44px tall without moving the line. */
export const TRACKER_LINK_CLASS =
  'py-3 text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 underline';

export const TRACKER_BOX_CLASS =
  'flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-md border border-indigo-200 bg-indigo-50 dark:border-indigo-800 dark:bg-indigo-950 px-3 py-2 text-sm text-gray-800 dark:text-gray-200';

export const COPY_FAILED = {
  title: 'Couldn’t copy',
  message: 'Your browser blocked the clipboard. Try again.',
};

/** Copies text for the participant's Claude chat, with the toast either way. */
export async function copyTrackerText(
  text: string,
  copiedTitle: string,
  failed: { title: string; message: string } = COPY_FAILED,
) {
  try {
    await navigator.clipboard.writeText(text);
    showToast({ type: 'ok', title: copiedTitle, message: '' });
  } catch {
    showToast({ type: 'warn', title: failed.title, message: failed.message });
  }
}

/** The tracker's numbers show only to students, and only while the tracker is on. */
export function showsTrackerGlance(course: { trackerEnabled?: boolean; level?: string }) {
  return !!course.trackerEnabled && course.level === 'student';
}

/** Never rejects: a failure leaves the extra card or line out. */
export async function loadTrackerGlance(
  courseId: UUID,
  weekStart?: string,
): Promise<TrackerGlance | undefined> {
  try {
    // An off tracker comes back as JSON null.
    return (await rpx.tracker.getMyTrackerGlance({ courseId, weekStart })) || undefined;
  } catch (err) {
    return swallowTrackerLoadFailure(err);
  }
}

export function trackerHref(courseUrl: string, personId?: string) {
  return personId
    ? `${courseUrl}/tracker?log=${encodeURIComponent(personId)}`
    : `${courseUrl}/tracker`;
}
